import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../ui/accordion';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Loader2, Plus, Trash2, Pencil, Copy, BedDouble, CalendarRange, Eye,
  Bed, Hotel, Home, Building, Building2, Warehouse, Mountain, Castle, Crown, Waves,
  ChevronLeft, ChevronRight, ImageIcon,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import ImageUploadPreviewEnhanced from '../ImageUploadPreviewEnhanced';

// Hotel Booking Engine v8, Workstream A, Phase A1 — Seller Portal
// RoomType + RoomRate manager. Only rendered for a Rental listing with
// `data.is_hotel=true` (see SchemaCardRenderer.jsx). Backend: routes/room_types.py.
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const BED_TYPE_OPTIONS = [
  { value: 'single', label: 'Single Bed' },
  { value: 'double', label: 'Double Bed' },
  { value: 'queen', label: 'Queen Size' },
  { value: 'king', label: 'King Size' },
  { value: 'twin', label: 'Twin Beds' },
];
const MEAL_PLAN_OPTIONS = [
  { value: 'ep', label: 'EP (Room Only)' },
  { value: 'cp', label: 'CP (Breakfast)' },
  { value: 'map', label: 'MAP (Breakfast + Dinner)' },
  { value: 'ap', label: 'AP (All Meals)' },
];
// Expanded, categorized room-amenity vocabulary (v8 Round-3). Kept as a
// flat list of groups so the toggle UI below can render mini section
// headers without changing the underlying `amenities: string[]` shape
// on `RoomType` (free-form, see models/room_type.py).
const ROOM_AMENITY_GROUPS = [
  { group: 'Climate & Views', items: ['AC', 'Balcony', 'Sea View', 'Mountain View', 'City View', 'Garden View'] },
  { group: 'Storage & Hanging', items: ['Wardrobe / Closet', 'Hangers', 'Luggage Rack', 'In-room Safe / Locker', 'Shoe Rack'] },
  { group: 'Workspace', items: ['Work Desk', 'Office Chair', 'Reading Lamp', 'Power Outlets', 'USB Charging Ports'] },
  { group: 'Seating', items: ['Sofa / Lounge Chair', 'Armchair', 'Dining Table', 'Balcony Seating'] },
  { group: 'Mirrors', items: ['Full-length Mirror', 'Dressing Mirror', 'Bathroom Mirror'] },
  { group: 'Electronics & Connectivity', items: ['TV', 'WiFi', 'Intercom / Telephone', 'Alarm Clock', 'Bluetooth Speaker'] },
  { group: 'Bathroom & Personal Care', items: ['Private Bathroom', 'Attached Toilet', 'Hot Water / Geyser', 'Bathtub', 'Rain Shower', 'Toiletries', 'Hairdryer', 'Bathrobe & Slippers'] },
  { group: 'Food & Beverage', items: ['Mini Bar', 'Room Service', 'Electric Kettle', 'Coffee / Tea Maker', 'Refrigerator'] },
  { group: 'Security & Utility', items: ['Smoke Detector', 'Fire Extinguisher', 'First Aid Kit', 'Door Lock / Keycard'] },
  { group: 'Laundry & DND', items: ['Laundry Bag', 'Iron & Ironing Board', 'Do Not Disturb Sign'] },
];
// Common hotel/resort room & suite type presets — picking one auto-fills
// Name + Description (both stay fully editable afterward). "Custom" skips
// auto-fill entirely so the seller can type their own. Icon is purely
// cosmetic (dropdown usability) and never persisted.
const ROOM_TYPE_PRESETS = [
  { name: 'Standard / Regular Room', icon: Bed, description: "The property's entry-level baseline. It is the most affordable option, offering standard amenities (bed, desk, bathroom) with a basic view or lower floor placement." },
  { name: 'Superior Room', icon: Bed, description: 'One step above the standard room. It typically grants slightly more square footage, updated furnishings, or a better placement in the building (like a higher floor or quieter side).' },
  { name: 'Deluxe Room', icon: BedDouble, description: "A premium tier room that is noticeably larger than a standard or superior room. It features the property's best views (e.g., ocean or city skyline), high-end finishes, and often a dedicated, comfortable seating area." },
  { name: 'Executive / Club Room', icon: Building, description: 'Rooms tailored specifically for business travelers, usually located on restricted higher floors. Booking this type often includes exclusive access to a "Club Lounge" for complimentary breakfast, drinks, and meeting spaces.' },
  { name: 'Junior Suite (Mini-Suite)', icon: Hotel, description: 'An evolutionary step between a deluxe room and a full suite. It is technically a single, large open-plan room, but features a distinct, clearly defined living/lounge zone.' },
  { name: 'Regular / Standard Suite', icon: Hotel, description: 'A full multi-room layout featuring a completely separate bedroom closed off from a dedicated living room.' },
  { name: 'Deluxe Suite', icon: Hotel, description: 'An elevated suite that offers significantly more square footage, higher-end interior design, upgraded bathroom fixtures (like a soaking tub), and premium panoramic views.' },
  { name: 'Executive Suite', icon: Building2, description: 'A massive suite built with distinct zones for relaxation and productivity, often including an ergonomic workspace, a small conference table, and club lounge privileges.' },
  { name: 'Presidential / Royal Suite', icon: Crown, description: 'The crown jewel and most expensive booking option on the property. It typically spans a massive footprint with multiple bedrooms, full dining rooms, custom high-end services (like a private butler), and maximum security features.' },
  { name: 'Penthouse Suite', icon: Crown, description: 'A highly exclusive, ultra-luxury suite situated entirely on the top floor(s) of the building, usually boasting massive private terraces and floor-to-ceiling windows.' },
  { name: 'Studio', icon: Home, description: 'An efficient, apartment-style open space that bundles the bed, a small living area, and a compact kitchenette into one single room.' },
  { name: 'Cottage', icon: Home, description: 'A charming, cozy, standalone lodging option usually situated in rural, mountainous, or countryside destinations. They emphasize a rustic aesthetic, privacy, and homelike comforts.' },
  { name: 'Bungalow', icon: Warehouse, description: 'A single-story standalone structure, often featuring a wide front porch or veranda. They are highly popular in tropical beach locations.' },
  { name: 'Villa', icon: Castle, description: 'A sprawling, high-end private estate or standalone house within a luxury resort footprint. Villas usually come equipped with multiple bedrooms, fully functional kitchens, private gardens, and their own dedicated private swimming pools.' },
  { name: 'Cabana', icon: Waves, description: 'A specialized room that opens up directly onto a swimming pool, private beach, or lagoon boardwalk.' },
  { name: 'Chalet', icon: Mountain, description: 'A standalone wooden alpine structure with steeply sloping roofs, custom-built for ski resorts and mountain landscapes.' },
];

const emptyRoomTypeForm = {
  name: '', description: '', physical_room_count: 1, max_adults: 2, max_children: 0,
  bed_type: '', bed_count: '', room_size_sqft: '', check_in_time: '', check_out_time: '',
  meal_plan: '', breakfast_included: false, amenities: [], images: [],
};
const emptyRateForm = {
  rate_type: 'date_range', name: '', start_date: '', end_date: '', applicable_weekdays: [],
  price_mode: 'explicit', nightly_price: '', percentage_adjustment: '',
  base_occupancy: '', extra_adult_price: '', extra_child_price: '', priority: 0, min_stay_nights: '',
};

export const RoomTypesCard = ({ listingId, onSaveDraftClick, saveDraftLoading = false }) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ratesByRoomType, setRatesByRoomType] = useState({});
  const [previewByRoomType, setPreviewByRoomType] = useState({});
  const [showNewRoomType, setShowNewRoomType] = useState(false);
  const [newRoomType, setNewRoomType] = useState(emptyRoomTypeForm);
  const [savingRoomType, setSavingRoomType] = useState(false);
  const [editingRoomTypeId, setEditingRoomTypeId] = useState(null); // null = create mode
  const [roomTypePreset, setRoomTypePreset] = useState('');
  const [rateDialogFor, setRateDialogFor] = useState(null); // room_type_id or null
  const [rateForm, setRateForm] = useState(emptyRateForm);
  const [savingRate, setSavingRate] = useState(false);
  const [editingRateId, setEditingRateId] = useState(null); // null = create mode
  const [applyingRateId, setApplyingRateId] = useState(null);
  const [duplicatingRoomTypeId, setDuplicatingRoomTypeId] = useState(null);
  const [calendarRoomTypeId, setCalendarRoomTypeId] = useState(null); // room_type_id or null
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [calendarDays, setCalendarDays] = useState({}); // { 'YYYY-MM-DD': dayObj }
  const [calendarLoading, setCalendarLoading] = useState(false);

  const fetchRoomTypes = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    try {
      const res = await apiCall(`/api/listings/${listingId}/room-types`);
      setRoomTypes(Array.isArray(res) ? res : []);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to load room types', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [listingId, apiCall, toast]);

  useEffect(() => { fetchRoomTypes(); }, [fetchRoomTypes]);

  const fetchRates = async (roomTypeId) => {
    try {
      const res = await apiCall(`/api/listings/${listingId}/room-types/${roomTypeId}/rates`);
      setRatesByRoomType((prev) => ({ ...prev, [roomTypeId]: Array.isArray(res) ? res : [] }));
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to load rates', variant: 'destructive' });
    }
  };

  const fetchPreview = async (roomTypeId) => {
    const today = new Date();
    const start = today.toISOString().slice(0, 10);
    const endDate = new Date(today.getTime() + 13 * 86400000);
    const end = endDate.toISOString().slice(0, 10);
    try {
      const res = await apiCall(`/api/listings/${listingId}/room-types/${roomTypeId}/rate-preview?start_date=${start}&end_date=${end}`);
      setPreviewByRoomType((prev) => ({ ...prev, [roomTypeId]: res.days || [] }));
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to load rate preview', variant: 'destructive' });
    }
  };

  const openRateCalendar = (roomTypeId) => {
    setCalendarRoomTypeId(roomTypeId);
    const d = new Date(); d.setDate(1);
    setCalendarMonth(d);
    fetchCalendarMonth(roomTypeId, d);
  };

  const fmtDate = (year, month, day) => {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  const fetchCalendarMonth = async (roomTypeId, monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const start = fmtDate(year, month, 1);
    const end = fmtDate(year, month, daysInMonth);
    setCalendarLoading(true);
    try {
      const res = await apiCall(`/api/listings/${listingId}/room-types/${roomTypeId}/rate-preview?start_date=${start}&end_date=${end}`);
      const byDate = {};
      (res.days || []).forEach((d) => { byDate[d.date] = d; });
      setCalendarDays(byDate);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to load rate calendar', variant: 'destructive' });
    } finally {
      setCalendarLoading(false);
    }
  };

  const navigateCalendarMonth = (direction) => {
    const next = new Date(calendarMonth);
    next.setMonth(next.getMonth() + direction);
    setCalendarMonth(next);
    fetchCalendarMonth(calendarRoomTypeId, next);
  };

  const buildCalendarGrid = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    // Monday-first grid (matches WEEKDAY_LABELS ordering elsewhere in this file).
    const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < leadingBlanks; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(fmtDate(year, month, day));
    }
    return cells;
  };

  const calendarCellStyle = (dayInfo) => {
    if (!dayInfo || dayInfo.price === null || dayInfo.basis === 'unbookable') {
      return 'bg-gray-50 border-gray-200 text-gray-400';
    }
    if (dayInfo.rate_type === 'base') {
      return 'bg-sky-50 border-sky-200 text-sky-900';
    }
    return 'bg-amber-50 border-amber-300 text-amber-900';
  };


  const openCreateRoomType = () => {
    setEditingRoomTypeId(null);
    setNewRoomType(emptyRoomTypeForm);
    setRoomTypePreset('');
    setShowNewRoomType(true);
  };

  const openEditRoomType = (rt) => {
    setEditingRoomTypeId(rt.id);
    setNewRoomType({
      name: rt.name || '',
      description: rt.description || '',
      physical_room_count: rt.physical_room_count ?? 1,
      max_adults: rt.max_adults ?? 2,
      max_children: rt.max_children ?? 0,
      bed_type: rt.bed_type || '',
      bed_count: rt.bed_count ?? '',
      room_size_sqft: rt.room_size_sqft ?? '',
      check_in_time: rt.check_in_time || '',
      check_out_time: rt.check_out_time || '',
      meal_plan: rt.meal_plan || '',
      breakfast_included: !!rt.breakfast_included,
      amenities: rt.amenities || [],
      images: rt.images || [],
    });
    setRoomTypePreset('');
    setShowNewRoomType(true);
  };

  const applyRoomTypePreset = (presetName) => {
    setRoomTypePreset(presetName);
    if (presetName === 'custom') return;
    const preset = ROOM_TYPE_PRESETS.find((p) => p.name === presetName);
    if (preset) {
      setNewRoomType((p) => ({ ...p, name: preset.name, description: preset.description }));
    }
  };

  const toggleRoomAmenity = (amenity) => {
    setNewRoomType((p) => ({
      ...p,
      amenities: p.amenities.includes(amenity) ? p.amenities.filter((a) => a !== amenity) : [...p.amenities, amenity],
    }));
  };

  const closeRoomTypeDialog = (open) => {
    setShowNewRoomType(open);
    if (!open) {
      setEditingRoomTypeId(null);
      setNewRoomType(emptyRoomTypeForm);
      setRoomTypePreset('');
    }
  };

  const handleSaveRoomType = async () => {
    if (!newRoomType.name.trim() || !newRoomType.physical_room_count) {
      toast({ title: 'Missing info', description: 'Name and physical room count are required', variant: 'destructive' });
      return;
    }
    setSavingRoomType(true);
    try {
      const payload = {
        ...newRoomType,
        physical_room_count: parseInt(newRoomType.physical_room_count, 10),
        max_adults: parseInt(newRoomType.max_adults, 10),
        max_children: parseInt(newRoomType.max_children, 10),
        bed_count: newRoomType.bed_count ? parseInt(newRoomType.bed_count, 10) : null,
        room_size_sqft: newRoomType.room_size_sqft ? parseFloat(newRoomType.room_size_sqft) : null,
        bed_type: newRoomType.bed_type || null,
        check_in_time: newRoomType.check_in_time || null,
        check_out_time: newRoomType.check_out_time || null,
        meal_plan: newRoomType.meal_plan || null,
      };
      if (editingRoomTypeId) {
        await apiCall(`/api/listings/${listingId}/room-types/${editingRoomTypeId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        toast({ title: 'Room type updated' });
      } else {
        await apiCall(`/api/listings/${listingId}/room-types`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        toast({ title: 'Room type created' });
      }
      closeRoomTypeDialog(false);
      fetchRoomTypes();
    } catch (error) {
      toast({ title: editingRoomTypeId ? 'Could not update room type' : 'Could not create room type', description: error.message, variant: 'destructive' });
    } finally {
      setSavingRoomType(false);
    }
  };

  const handleDeleteRoomType = async (roomTypeId) => {
    try {
      await apiCall(`/api/listings/${listingId}/room-types/${roomTypeId}`, { method: 'DELETE' });
      toast({ title: 'Room type deleted' });
      fetchRoomTypes();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleDuplicateRoomType = async (roomTypeId) => {
    setDuplicatingRoomTypeId(roomTypeId);
    try {
      const clone = await apiCall(`/api/listings/${listingId}/room-types/${roomTypeId}/duplicate`, { method: 'POST' });
      toast({ title: 'Room type duplicated', description: `Created "${clone.name}" with all its rate rules — just adjust the price.` });
      fetchRoomTypes();
    } catch (error) {
      toast({ title: 'Could not duplicate room type', description: error.message, variant: 'destructive' });
    } finally {
      setDuplicatingRoomTypeId(null);
    }
  };

  const openRateDialog = (roomTypeId, rateType) => {
    setEditingRateId(null);
    setRateForm({ ...emptyRateForm, rate_type: rateType });
    setRateDialogFor(roomTypeId);
  };

  const openEditRate = (roomTypeId, rate) => {
    setEditingRateId(rate.id);
    setRateForm({
      rate_type: rate.rate_type,
      name: rate.name || '',
      start_date: rate.start_date || '',
      end_date: rate.end_date || '',
      applicable_weekdays: rate.applicable_weekdays || [],
      price_mode: rate.percentage_adjustment != null ? 'percentage' : 'explicit',
      nightly_price: rate.nightly_price ?? '',
      percentage_adjustment: rate.percentage_adjustment ?? '',
      base_occupancy: rate.base_occupancy ?? '',
      extra_adult_price: rate.extra_adult_price ?? '',
      extra_child_price: rate.extra_child_price ?? '',
      priority: rate.priority ?? 0,
      min_stay_nights: rate.min_stay_nights ?? '',
    });
    setRateDialogFor(roomTypeId);
  };

  const handleSaveRate = async () => {
    const payload = { rate_type: rateForm.rate_type, priority: parseInt(rateForm.priority, 10) || 0 };
    if (rateForm.name.trim()) payload.name = rateForm.name.trim();
    if (rateForm.rate_type === 'date_range') {
      if (!rateForm.start_date || !rateForm.end_date) {
        toast({ title: 'Missing dates', description: 'Start and end date are required for a date-range rule', variant: 'destructive' });
        return;
      }
      payload.start_date = rateForm.start_date;
      payload.end_date = rateForm.end_date;
      if (rateForm.applicable_weekdays.length > 0) payload.applicable_weekdays = rateForm.applicable_weekdays;
      if (rateForm.min_stay_nights) payload.min_stay_nights = parseInt(rateForm.min_stay_nights, 10);
    }
    if (rateForm.price_mode === 'percentage' && rateForm.rate_type === 'date_range') {
      payload.percentage_adjustment = parseFloat(rateForm.percentage_adjustment);
    } else {
      payload.nightly_price = parseFloat(rateForm.nightly_price);
    }
    if (rateForm.base_occupancy) payload.base_occupancy = parseInt(rateForm.base_occupancy, 10);
    if (rateForm.extra_adult_price) payload.extra_adult_price = parseFloat(rateForm.extra_adult_price);
    if (rateForm.extra_child_price) payload.extra_child_price = parseFloat(rateForm.extra_child_price);

    setSavingRate(true);
    try {
      if (editingRateId) {
        await apiCall(`/api/listings/${listingId}/room-types/${rateDialogFor}/rates/${editingRateId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        toast({ title: 'Rate updated' });
      } else {
        await apiCall(`/api/listings/${listingId}/room-types/${rateDialogFor}/rates`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        toast({ title: 'Rate saved' });
      }
      const roomTypeId = rateDialogFor;
      setRateDialogFor(null);
      setEditingRateId(null);
      fetchRates(roomTypeId);
    } catch (error) {
      toast({ title: 'Could not save rate', description: error.message, variant: 'destructive' });
    } finally {
      setSavingRate(false);
    }
  };

  const handleApplyRateToAll = async (roomTypeId, rateId) => {
    setApplyingRateId(rateId);
    try {
      const res = await apiCall(`/api/listings/${listingId}/room-types/${roomTypeId}/rates/${rateId}/apply-to-all`, { method: 'POST' });
      const appliedCount = res?.applied_to?.length || 0;
      const skippedCount = res?.skipped?.length || 0;
      toast({
        title: 'Rule applied',
        description: `Copied to ${appliedCount} room type${appliedCount === 1 ? '' : 's'}` +
          (skippedCount ? `, skipped ${skippedCount} (already has a base rate)` : ''),
      });
      roomTypes.forEach((rt) => { if (ratesByRoomType[rt.id] || rt.id === roomTypeId) fetchRates(rt.id); });
    } catch (error) {
      toast({ title: 'Could not apply rule', description: error.message, variant: 'destructive' });
    } finally {
      setApplyingRateId(null);
    }
  };

  const handleDeleteRate = async (roomTypeId, rateId) => {
    try {
      await apiCall(`/api/listings/${listingId}/room-types/${roomTypeId}/rates/${rateId}`, { method: 'DELETE' });
      fetchRates(roomTypeId);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to delete rate', variant: 'destructive' });
    }
  };

  const toggleWeekday = (idx) => {
    setRateForm((prev) => ({
      ...prev,
      applicable_weekdays: prev.applicable_weekdays.includes(idx)
        ? prev.applicable_weekdays.filter((w) => w !== idx)
        : [...prev.applicable_weekdays, idx],
    }));
  };

  if (!listingId) {
    return (
      <Card data-testid="room-types-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><BedDouble className="h-4 w-4" /> Room Types</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-500" data-testid="room-types-save-first-note">
            Save this hotel listing first, then come back here to add room types and rates.
          </p>
          {onSaveDraftClick && (
            <Button
              type="button" size="sm" onClick={onSaveDraftClick} disabled={saveDraftLoading}
              data-testid="room-types-save-draft-btn"
            >
              {saveDraftLoading && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save Draft to Continue
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="room-types-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><BedDouble className="h-4 w-4" /> Room Types</CardTitle>
          <CardDescription>
            Up to 15 room types. Each room type has its own base rate plus weekday, seasonal and
            festival-date overrides — the engine always picks exactly one deterministic rate per night.
          </CardDescription>
        </div>
        <Button
          type="button" size="sm" onClick={openCreateRoomType}
          disabled={roomTypes.length >= 15}
          data-testid="add-room-type-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Room Type
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-400" data-testid="room-types-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : roomTypes.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="room-types-empty">No room types yet. Click "Add Room Type" to create your first one.</p>
        ) : (
          <Accordion type="multiple" className="space-y-2">
            {roomTypes.map((rt) => (
              <AccordionItem key={rt.id} value={rt.id} className="border rounded-lg px-3" data-testid={`room-type-row-${rt.id}`}>
                <AccordionTrigger
                  onClick={() => { if (!ratesByRoomType[rt.id]) fetchRates(rt.id); }}
                  data-testid={`room-type-expand-${rt.id}`}
                >
                  <div className="flex items-center gap-3 text-left flex-wrap">
                    <span className="font-medium" data-testid={`room-type-name-${rt.id}`}>{rt.name}</span>
                    <Badge variant="secondary">{rt.physical_room_count} room{rt.physical_room_count === 1 ? '' : 's'}</Badge>
                    <Badge variant="outline">Max {rt.max_adults} adults{rt.max_children ? ` + ${rt.max_children} children` : ''}</Badge>
                    {rt.bed_type && <Badge variant="outline">{BED_TYPE_OPTIONS.find((o) => o.value === rt.bed_type)?.label || rt.bed_type}</Badge>}
                    {rt.room_size_sqft ? <Badge variant="outline">{rt.room_size_sqft} sq ft</Badge> : null}
                    {rt.meal_plan && <Badge variant="outline">{rt.meal_plan.toUpperCase()}</Badge>}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  {(rt.description || (rt.amenities || []).length > 0 || (rt.images || []).length > 0) && (
                    <div className="text-sm text-gray-600 space-y-2" data-testid={`room-type-details-${rt.id}`}>
                      {(rt.images || []).length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`room-type-photo-strip-${rt.id}`}>
                          {rt.images.map((url, idx) => (
                            <img
                              key={`${url}-${idx}`}
                              src={url}
                              alt={`${rt.name} photo ${idx + 1}`}
                              className="h-16 w-20 object-cover rounded-md border border-gray-200 flex-shrink-0"
                              data-testid={`room-type-photo-thumb-${rt.id}-${idx}`}
                            />
                          ))}
                        </div>
                      )}
                      {rt.description && <p>{rt.description}</p>}
                      {(rt.amenities || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {rt.amenities.map((a) => <Badge key={a} variant="outline" className="font-normal">{a}</Badge>)}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => fetchPreview(rt.id)} data-testid={`preview-rates-btn-${rt.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Preview next 14 days
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => openRateCalendar(rt.id)} data-testid={`open-rate-calendar-btn-${rt.id}`}>
                      <CalendarRange className="h-3.5 w-3.5 mr-1" /> Rate Calendar
                    </Button>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => openEditRoomType(rt)}
                      data-testid={`edit-room-type-btn-${rt.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => handleDuplicateRoomType(rt.id)}
                      disabled={duplicatingRoomTypeId === rt.id || roomTypes.length >= 15}
                      data-testid={`duplicate-room-type-btn-${rt.id}`}
                    >
                      {duplicatingRoomTypeId === rt.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Copy className="h-3.5 w-3.5 mr-1" />} Duplicate
                    </Button>
                    <Button
                      type="button" size="sm" variant="destructive"
                      onClick={() => handleDeleteRoomType(rt.id)}
                      data-testid={`delete-room-type-btn-${rt.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {previewByRoomType[rt.id] && (
                    <div className="overflow-x-auto" data-testid={`rate-preview-table-${rt.id}`}>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Price</TableHead>
                            <TableHead>Rule</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {previewByRoomType[rt.id].map((d) => (
                            <TableRow key={d.date} data-testid={`rate-preview-day-${rt.id}-${d.date}`}>
                              <TableCell>{d.date}</TableCell>
                              <TableCell>{d.price !== null ? `₹${d.price}` : <span className="text-red-500">Unbookable</span>}</TableCell>
                              <TableCell className="text-xs text-gray-500">{d.basis === 'percentage' ? 'Seasonal % rule' : d.basis === 'explicit' ? (d.rate_type === 'base' ? 'Base rate' : 'Date rule') : '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => openRateDialog(rt.id, 'base')} data-testid={`add-base-rate-btn-${rt.id}`}>
                      <CalendarRange className="h-3.5 w-3.5 mr-1" /> Set Base Rate
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => openRateDialog(rt.id, 'date_range')} data-testid={`add-date-rate-btn-${rt.id}`}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Weekday / Seasonal / Festival Rule
                    </Button>
                  </div>

                  {(ratesByRoomType[rt.id] || []).length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead>Weekdays</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Priority</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(ratesByRoomType[rt.id] || []).map((rate) => (
                          <TableRow key={rate.id} data-testid={`rate-row-${rate.id}`}>
                            <TableCell>{rate.rate_type === 'base' ? 'Base' : 'Date Rule'}</TableCell>
                            <TableCell data-testid={`rate-name-${rate.id}`}>{rate.name || <span className="text-gray-400">—</span>}</TableCell>
                            <TableCell>{rate.rate_type === 'base' ? 'Every day' : `${rate.start_date} → ${rate.end_date}`}</TableCell>
                            <TableCell>{rate.applicable_weekdays?.length ? rate.applicable_weekdays.map((w) => WEEKDAY_LABELS[w]).join(', ') : 'All'}</TableCell>
                            <TableCell>{rate.percentage_adjustment != null ? `${rate.percentage_adjustment > 0 ? '+' : ''}${rate.percentage_adjustment}%` : `₹${rate.nightly_price}`}</TableCell>
                            <TableCell>{rate.priority}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditRate(rt.id, rate)} data-testid={`edit-rate-btn-${rate.id}`} title="Edit rule">
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                {roomTypes.length > 1 && (
                                  <Button
                                    type="button" variant="ghost" size="icon" className="h-7 w-7"
                                    onClick={() => handleApplyRateToAll(rt.id, rate.id)}
                                    disabled={applyingRateId === rate.id}
                                    data-testid={`apply-rate-all-btn-${rate.id}`}
                                    title="Apply this rule to all other room types"
                                  >
                                    {applyingRateId === rate.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                                  </Button>
                                )}
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteRate(rt.id, rate.id)} data-testid={`delete-rate-btn-${rate.id}`} title="Delete rule">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>

      {/* New / Edit Room Type dialog */}
      <Dialog open={showNewRoomType} onOpenChange={closeRoomTypeDialog}>
        <DialogContent data-testid="new-room-type-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingRoomTypeId ? 'Edit Room Type' : 'Add Room Type'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Room type preset</Label>
              <Select value={roomTypePreset} onValueChange={applyRoomTypePreset}>
                <SelectTrigger data-testid="room-type-preset-select">
                  <SelectValue placeholder="Pick a common room/suite type, or Custom" />
                </SelectTrigger>
                <SelectContent>
                  {ROOM_TYPE_PRESETS.map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      <span className="flex items-center gap-2"><p.icon className="h-3.5 w-3.5 text-gray-500" /> {p.name}</span>
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom...</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Picking one fills in Name + Description below — both stay fully editable.</p>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={newRoomType.name} onChange={(e) => setNewRoomType((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Deluxe Double Room" data-testid="new-room-type-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newRoomType.description} onChange={(e) => setNewRoomType((p) => ({ ...p, description: e.target.value }))} data-testid="new-room-type-description" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Rooms available</Label>
                <Input type="number" min={1} value={newRoomType.physical_room_count} onChange={(e) => setNewRoomType((p) => ({ ...p, physical_room_count: e.target.value }))} data-testid="new-room-type-count" />
              </div>
              <div>
                <Label>Max adults</Label>
                <Input type="number" min={1} value={newRoomType.max_adults} onChange={(e) => setNewRoomType((p) => ({ ...p, max_adults: e.target.value }))} data-testid="new-room-type-max-adults" />
              </div>
              <div>
                <Label>Max children</Label>
                <Input type="number" min={0} value={newRoomType.max_children} onChange={(e) => setNewRoomType((p) => ({ ...p, max_children: e.target.value }))} data-testid="new-room-type-max-children" />
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-gray-500 mb-2">ROOM DETAILS</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Bed type</Label>
                  <Select value={newRoomType.bed_type} onValueChange={(v) => setNewRoomType((p) => ({ ...p, bed_type: v }))}>
                    <SelectTrigger data-testid="new-room-type-bed-type"><SelectValue placeholder="Select bed type" /></SelectTrigger>
                    <SelectContent>
                      {BED_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bed count</Label>
                  <Input type="number" min={0} value={newRoomType.bed_count} onChange={(e) => setNewRoomType((p) => ({ ...p, bed_count: e.target.value }))} data-testid="new-room-type-bed-count" />
                </div>
                <div>
                  <Label>Room size (sq ft)</Label>
                  <Input type="number" min={0} value={newRoomType.room_size_sqft} onChange={(e) => setNewRoomType((p) => ({ ...p, room_size_sqft: e.target.value }))} placeholder="e.g., 300" data-testid="new-room-type-size" />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-gray-500 mb-2">CHECK-IN / MEALS</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Check-in time</Label>
                  <Input type="time" value={newRoomType.check_in_time} onChange={(e) => setNewRoomType((p) => ({ ...p, check_in_time: e.target.value }))} data-testid="new-room-type-checkin" />
                </div>
                <div>
                  <Label>Check-out time</Label>
                  <Input type="time" value={newRoomType.check_out_time} onChange={(e) => setNewRoomType((p) => ({ ...p, check_out_time: e.target.value }))} data-testid="new-room-type-checkout" />
                </div>
              </div>
              <div className="mt-2">
                <Label>Meal plan</Label>
                <Select value={newRoomType.meal_plan} onValueChange={(v) => setNewRoomType((p) => ({ ...p, meal_plan: v }))}>
                  <SelectTrigger data-testid="new-room-type-meal-plan"><SelectValue placeholder="Select meal plan" /></SelectTrigger>
                  <SelectContent>
                    {MEAL_PLAN_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Checkbox
                  checked={newRoomType.breakfast_included}
                  onCheckedChange={(v) => setNewRoomType((p) => ({ ...p, breakfast_included: !!v }))}
                  data-testid="new-room-type-breakfast"
                />
                <Label>Breakfast included</Label>
              </div>
            </div>

            <div className="pt-2 border-t">
              <Label className="flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" /> Room photos</Label>
              <p className="text-xs text-gray-500 mb-2">Show guests exactly what they're booking. The first photo is used as the main thumbnail.</p>
              <div data-testid="room-type-photo-uploader">
                <ImageUploadPreviewEnhanced
                  existingFiles={newRoomType.images}
                  onFilesChange={(urls) => setNewRoomType((p) => ({ ...p, images: urls }))}
                  folder="room-types"
                />
              </div>
            </div>

            <div className="pt-2 border-t">
              <Label>Room amenities</Label>
              <div className="space-y-2.5 mt-1">
                {ROOM_AMENITY_GROUPS.map(({ group, items }) => (
                  <div key={group}>
                    <p className="text-xs font-medium text-gray-400 mb-1">{group.toUpperCase()}</p>
                    <div className="flex flex-wrap gap-2">
                      {items.map((amenity) => (
                        <Badge
                          key={amenity}
                          variant={newRoomType.amenities.includes(amenity) ? 'default' : 'outline'}
                          className="cursor-pointer select-none"
                          onClick={() => toggleRoomAmenity(amenity)}
                          data-testid={`room-type-amenity-${amenity.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                        >
                          {amenity}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" onClick={handleSaveRoomType} disabled={savingRoomType} data-testid="save-new-room-type-btn">
              {savingRoomType && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {editingRoomTypeId ? 'Save Changes' : 'Create Room Type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New / Edit Rate dialog */}
      <Dialog open={!!rateDialogFor} onOpenChange={(open) => { if (!open) { setRateDialogFor(null); setEditingRateId(null); } }}>
        <DialogContent data-testid="new-rate-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingRateId ? 'Edit Rule' : (rateForm.rate_type === 'base' ? 'Set Base Rate' : 'Add Date Rule')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {rateForm.rate_type === 'date_range' && (
              <>
                <div>
                  <Label>Rule name (optional)</Label>
                  <Input
                    value={rateForm.name} onChange={(e) => setRateForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Diwali, Independence Long Weekend, Winter Season"
                    data-testid="rate-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Start date</Label>
                    <Input type="date" value={rateForm.start_date} onChange={(e) => setRateForm((p) => ({ ...p, start_date: e.target.value }))} data-testid="rate-start-date" />
                  </div>
                  <div>
                    <Label>End date</Label>
                    <Input type="date" value={rateForm.end_date} onChange={(e) => setRateForm((p) => ({ ...p, end_date: e.target.value }))} data-testid="rate-end-date" />
                  </div>
                </div>
                <div>
                  <Label>Applicable weekdays (leave blank = every day in range)</Label>
                  <div className="flex gap-2 flex-wrap mt-1">
                    {WEEKDAY_LABELS.map((label, idx) => (
                      <label key={label} className="flex items-center gap-1 text-xs" data-testid={`rate-weekday-${idx}`}>
                        <Checkbox checked={rateForm.applicable_weekdays.includes(idx)} onCheckedChange={() => toggleWeekday(idx)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Price type</Label>
                  <Select value={rateForm.price_mode} onValueChange={(v) => setRateForm((p) => ({ ...p, price_mode: v }))}>
                    <SelectTrigger data-testid="rate-price-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="explicit">Explicit nightly price</SelectItem>
                      <SelectItem value="percentage">% adjustment off base rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Minimum stay (nights, optional)</Label>
                  <Input type="number" min={1} value={rateForm.min_stay_nights} onChange={(e) => setRateForm((p) => ({ ...p, min_stay_nights: e.target.value }))} data-testid="rate-min-stay" />
                </div>
              </>
            )}
            {rateForm.price_mode === 'percentage' && rateForm.rate_type === 'date_range' ? (
              <div>
                <Label>Percentage adjustment (e.g. 20 for +20%, -15 for -15%)</Label>
                <Input type="number" value={rateForm.percentage_adjustment} onChange={(e) => setRateForm((p) => ({ ...p, percentage_adjustment: e.target.value }))} data-testid="rate-percentage" />
              </div>
            ) : (
              <div>
                <Label>Nightly price (₹)</Label>
                <Input type="number" min={0} value={rateForm.nightly_price} onChange={(e) => setRateForm((p) => ({ ...p, nightly_price: e.target.value }))} data-testid="rate-nightly-price" />
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Base occupancy</Label>
                <Input type="number" min={1} value={rateForm.base_occupancy} onChange={(e) => setRateForm((p) => ({ ...p, base_occupancy: e.target.value }))} data-testid="rate-base-occupancy" />
              </div>
              <div>
                <Label>Extra adult ₹</Label>
                <Input type="number" min={0} value={rateForm.extra_adult_price} onChange={(e) => setRateForm((p) => ({ ...p, extra_adult_price: e.target.value }))} data-testid="rate-extra-adult" />
              </div>
              <div>
                <Label>Extra child ₹</Label>
                <Input type="number" min={0} value={rateForm.extra_child_price} onChange={(e) => setRateForm((p) => ({ ...p, extra_child_price: e.target.value }))} data-testid="rate-extra-child" />
              </div>
            </div>
            {rateForm.rate_type === 'date_range' && (
              <div>
                <Label>Priority (higher wins over lower when dates overlap)</Label>
                <Input type="number" min={0} value={rateForm.priority} onChange={(e) => setRateForm((p) => ({ ...p, priority: e.target.value }))} data-testid="rate-priority" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" onClick={handleSaveRate} disabled={savingRate} data-testid="save-rate-btn">
              {savingRate && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {editingRateId ? 'Save Changes' : 'Save Rate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Calendar dialog — month view sourced from the SAME
          deterministic pricing engine as "Preview next 14 days"
          (GET .../rate-preview), just rendered as a calendar grid
          instead of a table. */}
      <Dialog open={!!calendarRoomTypeId} onOpenChange={(open) => { if (!open) setCalendarRoomTypeId(null); }}>
        <DialogContent data-testid="rate-calendar-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-6">
              <span className="flex items-center gap-2"><CalendarRange className="h-4 w-4" /> Rate Calendar</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <Button type="button" size="icon" variant="outline" className="h-7 w-7" onClick={() => navigateCalendarMonth(-1)} data-testid="rate-calendar-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium text-sm" data-testid="rate-calendar-month-label">
              {calendarMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <Button type="button" size="icon" variant="outline" className="h-7 w-7" onClick={() => navigateCalendarMonth(1)} data-testid="rate-calendar-next-month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-3 text-xs mb-2 flex-wrap" data-testid="rate-calendar-legend">
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-sky-50 border border-sky-200 inline-block" /> Base rate</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-amber-50 border border-amber-300 inline-block" /> Seasonal / Festival / Weekday rule</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-gray-50 border border-gray-200 inline-block" /> Unbookable</span>
          </div>

          {calendarLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-400" data-testid="rate-calendar-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <div className="grid grid-cols-7 gap-1.5" data-testid="rate-calendar-grid">
              {WEEKDAY_LABELS.map((label) => (
                <div key={label} className="text-center text-xs font-medium text-gray-400 pb-1">{label}</div>
              ))}
              {buildCalendarGrid().map((dateStr, idx) => {
                if (!dateStr) return <div key={`blank-${idx}`} />;
                const dayInfo = calendarDays[dateStr];
                const dayNum = parseInt(dateStr.slice(-2), 10);
                return (
                  <div
                    key={dateStr}
                    className={`rounded-md border p-1.5 min-h-[64px] text-xs ${calendarCellStyle(dayInfo)}`}
                    data-testid={`rate-calendar-day-${dateStr}`}
                    title={dayInfo?.name || (dayInfo?.rate_type === 'base' ? 'Base rate' : '')}
                  >
                    <div className="font-medium">{dayNum}</div>
                    {dayInfo && dayInfo.price !== null ? (
                      <>
                        <div className="font-semibold">₹{dayInfo.price}</div>
                        {dayInfo.rate_type === 'date_range' && (
                          <div className="truncate text-[10px] opacity-80">{dayInfo.name || 'Date rule'}</div>
                        )}
                      </>
                    ) : (
                      <div className="text-[10px]">Unbookable</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RoomTypesCard;
